import type { CostType } from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { memo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableActionsCell, TableCell, TableRow } from "@/components/ui/table";
import { getCostApiKeyLabel } from "@/lib/constants/cost-api-keys";
import { useLocalize } from "@/lib/i18n/use-localize";
import type { ApiCostValue } from "@/lib/utils/compute-api-cost-values";

export type CostValueEntry = { calculatedValue: number; overriddenValue: number | null };

type CostRowProps = {
  costType: CostType;
  apiValue: ApiCostValue | undefined;
  entry: CostValueEntry | undefined;
  categoryLabel: string;
  removeLabel: string;
  addLabel: string;
  overrideMustBeNonNegativeLabel: string;
  onOverrideChange: (costTypeId: string, value: string) => void;
  onRemove: (costTypeId: string) => void;
  onAdd: (costTypeId: string, fallbackValue: number | null) => void;
};

function CostRowImpl({
  costType,
  apiValue,
  entry,
  categoryLabel,
  removeLabel,
  addLabel,
  overrideMustBeNonNegativeLabel,
  onOverrideChange,
  onRemove,
  onAdd,
}: CostRowProps) {
  const localize = useLocalize();
  const [draft, setDraft] = useState<string | undefined>(undefined);

  const isAssigned = entry !== undefined;
  const calculatedValue = isAssigned ? (entry?.calculatedValue ?? 0) : (apiValue?.value ?? null);
  const overriddenValue = entry?.overriddenValue ?? null;
  const overrideInvalid = overriddenValue !== null && overriddenValue < 0;
  const displayValue =
    draft !== undefined ? draft : overriddenValue !== null ? overriddenValue.toFixed(2) : "";

  return (
    <TableRow className={isAssigned ? undefined : "bg-muted/20"}>
      <TableCell>
        <div>
          <span className="font-medium">{localize(costType.nameI18n)}</span>
          {apiValue?.sourceKey && (
            <span className="block text-xs text-muted-foreground">
              {getCostApiKeyLabel(apiValue.sourceKey)}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline">{categoryLabel}</Badge>
      </TableCell>
      <TableCell className="text-right text-muted-foreground">
        {calculatedValue !== null ? `${calculatedValue.toFixed(2)}%` : "—"}
      </TableCell>
      <TableCell className="text-right">
        {isAssigned ? (
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center justify-end gap-1">
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className={`w-24 text-right ${overrideInvalid ? "border-destructive" : ""}`}
                placeholder="—"
                value={displayValue}
                onChange={(e) => {
                  setDraft(e.target.value);
                  onOverrideChange(costType.id, e.target.value);
                }}
                onBlur={() => setDraft(undefined)}
                aria-invalid={overrideInvalid}
              />
              <span className="text-muted-foreground">%</span>
            </div>
            {overrideInvalid && (
              <span className="text-xs text-destructive">{overrideMustBeNonNegativeLabel}</span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableActionsCell>
        {isAssigned ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            tooltip={removeLabel}
            onClick={() => onRemove(costType.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => onAdd(costType.id, apiValue?.value ?? null)}
          >
            {addLabel}
          </Button>
        )}
      </TableActionsCell>
    </TableRow>
  );
}

export const CostRow = memo(CostRowImpl);
